<template>
    <AiWorkspaceShell>
        <template #rail>
            <AiChatHistoryList
                :chats="chats"
                :active-chat-id="activeChat?.id ?? null"
                :q="q"
                :active-tag-ids="activeTagIds"
                :available-tags="availableTags"
                @new-chat="newChat"
            />
        </template>

        <template #main>
            <!-- The whole page scrolls; the chat header and the composer are
                 sticky and the messages travel underneath them. The column is
                 capped at 800px so lines stay readable on wide monitors. -->
            <div
                class="flex flex-col"
                :class="hasConversation ? 'lg:min-h-[calc(100vh-4rem)]' : ''"
            >
                <template v-if="hasConversation">
                    <div
                        class="mx-auto flex w-full min-w-0 max-w-[800px] flex-1 flex-col"
                    >
                        <div
                            class="sticky top-[calc(64px-1.5rem)] z-10 -mt-6 bg-gray-100 pt-6 md:top-16"
                        >
                            <AiChatHeader
                                :chat-id="activeChat?.id ?? null"
                                :title="activeChat?.title"
                                :created-at="activeChat?.created_at"
                                :feedback="activeChat?.feedback ?? null"
                                @select-capability="onCapability"
                            />
                        </div>
                        <AiChatConversation
                            :messages="displayedMessages"
                            :pending="busy"
                            :chat-id="activeChat?.id ?? null"
                            @answered="onAnswered"
                        />
                        <AiTagEditor
                            v-if="activeChat"
                            :entity-key="activeChat.id"
                            :submit-url="
                                route('ai-chat.tags.update', {
                                    chat: activeChat.id,
                                })
                            "
                            :tags="activeChat.tags"
                            :available-tags="availableTags"
                            collapsible
                        />
                    </div>
                </template>

                <!-- Empty state / welcome totem: greeting, suggestions and the
                     composer sit together as one compact, centred block. -->
                <template v-else>
                    <div
                        class="relative mx-auto mt-[148px] w-full max-w-[800px]"
                    >
                        <div class="absolute right-0 top-0 z-10">
                            <AiChatCapabilitiesButton
                                :disabled="limitReached"
                                @select="onCapability"
                            />
                        </div>
                        <AiChatWelcome
                            :recent-skills="recentSkills"
                            :disabled="limitReached"
                            @select="onExample"
                            @select-command="onCommandExample"
                            @select-skill="onSelectSkill"
                        />
                    </div>
                </template>

                <div
                    class="mx-auto w-full max-w-[800px]"
                    :class="
                        hasConversation
                            ? 'sticky bottom-0 z-10 bg-gray-100 pt-2'
                            : 'mt-4'
                    "
                >
                    <div
                        v-if="limitReached"
                        class="mb-2 rounded-lg bg-[#FF421E]/10 px-3 py-2 text-xs font-medium text-[#FF421E]"
                    >
                        {{ t("vue.ai_chat_limit_reached") }}
                    </div>
                    <AiChatComposer
                        ref="composer"
                        :disabled="sending || limitReached"
                        :error="errorMessage"
                        :skill="selectedSkill"
                        :upload-progress="uploadProgress"
                        @send="onSend"
                        @update:skill="selectedSkill = $event"
                    />
                    <p class="mt-2 pb-1 text-center text-xs text-gray-400">
                        <span
                            v-if="usageLabel"
                            class="font-medium text-gray-500"
                            >{{ usageLabel }} ·
                        </span>
                        {{ t("vue.ai_chat_disclaimer") }}
                    </p>
                </div>
            </div>

            <AiChatCapabilityTargetDialog
                :prompt="capabilityPrompt"
                @choose="onCapabilityTarget"
                @close="capabilityPrompt = null"
            />
        </template>
    </AiWorkspaceShell>
</template>

<script lang="ts" setup>
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import AiChatComposer from "@/Pages/AiChat/components/AiChatComposer.vue";
import type {
    AiCapabilityTarget,
    IAiChatDetail,
    IAiChatListItem,
    IAiChatMessage,
} from "@/types/AiChat/IAiChat";
import type { IAiTag } from "@/types/AiChat/IAiTag";
import type {
    IAiSkillAttachment,
    IAiSkillListItem,
} from "@/types/AiChat/IAiSkill";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { create } from "@/composables/useNotifications";
import { isAiSlashCommandDraft } from "@/composables/useAiSlashCommands";
import { useTranslations } from "@/composables/useTranslations";

const { t } = useTranslations();

const props = defineProps<{
    chats: IAiChatListItem[];
    activeChat: IAiChatDetail | null;
    pending: boolean;
    q: string | null;
    activeTagIds: number[];
    availableTags: IAiTag[];
    recentSkills: IAiSkillListItem[];
}>();

// Trial teams see their message usage; past the cap they can read history but
// not send new messages.
const aiAccess = computed(() => usePage<IRhizomPageProps>().props.aiChat);
const limitReached = computed(() => aiAccess.value?.limit_reached === true);
const usageLabel = computed<string | null>(() =>
    aiAccess.value?.level === "limited"
        ? `${aiAccess.value.used}/${aiAccess.value.limit} ${t("vue.ai_chat_usage_messages")}`
        : null,
);

// The skill attached to the next message. Carried across the new-chat navigation
// (see docs/app/ai-skills.md) when picked from the empty-state offering.
const PENDING_SKILL_KEY = "ai_chat_pending_skill";
const selectedSkill = ref<IAiSkillAttachment | null>(null);

// The capability question waiting for the user to say where it should land, and
// the same stash-across-navigation trick the skill above uses for a new chat.
const PENDING_PROMPT_KEY = "ai_chat_pending_prompt";
const capabilityPrompt = ref<string | null>(null);

const sending = ref(false);
const busy = computed(() => props.pending || sending.value);

// Multipart upload progress (0-100) while an image is being sent; null otherwise.
const uploadProgress = ref<number | null>(null);
const composer = ref<InstanceType<typeof AiChatComposer> | null>(null);

const trackUploadProgress = (
    image: File | null,
): ((event?: { percentage?: number }) => void) => {
    return (event) => {
        uploadProgress.value =
            image !== null ? (event?.percentage ?? null) : null;
    };
};

// Optimistically rendered user messages (shown instantly, before the server
// round-trip persists and returns them).
let tempId = -1;
const optimistic = ref<IAiChatMessage[]>([]);

const displayedMessages = computed<IAiChatMessage[]>(() => [
    ...(props.activeChat?.messages ?? []),
    ...optimistic.value,
]);

const hasConversation = computed(
    () => props.activeChat !== null && displayedMessages.value.length > 0,
);

const errorMessage = computed(() => {
    const errors = usePage().props.errors as Record<string, string> | undefined;
    return errors?.message ?? errors?.command ?? errors?.image;
});

let pollTimer: number | undefined;
let pollCount = 0;
const POLL_INTERVAL_MS = 2500;
// ~3 minutes. A reply job lives at most ~75 s server-side; polling past this
// point means the job was lost, so we stop instead of hammering the server.
const MAX_PENDING_POLLS = 72;

const stopPolling = () => {
    if (pollTimer !== undefined) {
        window.clearInterval(pollTimer);
        pollTimer = undefined;
    }
    pollCount = 0;
};

const startPolling = () => {
    if (pollTimer !== undefined) {
        return;
    }

    pollTimer = window.setInterval(() => {
        pollCount += 1;
        if (pollCount > MAX_PENDING_POLLS) {
            stopPolling();
            create({ title: t("vue.ai_chat_reply_timeout"), type: "error" });
            return;
        }
        router.reload({
            only: ["activeChat", "pending"],
            preserveScroll: true,
        });
    }, POLL_INTERVAL_MS);
};

watch(
    () => props.pending,
    (pending) => {
        if (pending) {
            startPolling();
        } else {
            stopPolling();
        }
    },
    { immediate: true },
);

onUnmounted(stopPolling);

const newChat = () => {
    router.post(
        route("ai-chat.store"),
        {},
        {
            preserveScroll: true,
        },
    );
};

const startNewChatWithMessage = (payload: {
    message: string;
    image: File | null;
}) => {
    sending.value = true;

    router.post(
        route("ai-chat.store"),
        {
            message: payload.message,
            image: payload.image,
            skill_id: selectedSkill.value?.id ?? null,
        },
        {
            preserveScroll: true,
            onProgress: trackUploadProgress(payload.image),
            onSuccess: () => {
                // The composer keeps its content until the chat exists, so a
                // failed upload never silently swallows the message or image.
                composer.value?.reset();
                selectedSkill.value = null;
            },
            onFinish: () => {
                sending.value = false;
                uploadProgress.value = null;
            },
        },
    );
};

const sendMessage = (payload: { message: string; image: File | null }) => {
    if (!props.activeChat) {
        return;
    }

    // Local preview for the optimistic bubble; revoked once the server round-trip
    // returns the persisted message (which carries the real proxy URL).
    const localImageUrl =
        payload.image !== null ? URL.createObjectURL(payload.image) : null;
    const skill = selectedSkill.value;

    optimistic.value = [
        ...optimistic.value,
        {
            id: tempId--,
            type: "user",
            content: payload.message !== "" ? payload.message : null,
            tool_name: null,
            created_at: null,
            attachment:
                payload.image !== null && localImageUrl !== null
                    ? { url: localImageUrl, filename: payload.image.name }
                    : null,
            skill,
        },
    ];
    sending.value = true;

    // Inertia automatically switches to a multipart FormData request when the
    // payload contains a File instance.
    router.post(
        route("ai-chat.messages.store", { chat: props.activeChat.id }),
        {
            message: payload.message,
            image: payload.image,
            skill_id: skill?.id ?? null,
        },
        {
            preserveScroll: true,
            onProgress: trackUploadProgress(payload.image),
            onSuccess: () => {
                // Server data now contains the persisted message.
                optimistic.value = [];
                selectedSkill.value = null;
            },
            onError: () => {
                // The message was rejected — drop the optimistic bubble so the
                // conversation does not pretend it was delivered.
                optimistic.value = [];
            },
            onFinish: () => {
                sending.value = false;
                uploadProgress.value = null;
                if (localImageUrl !== null) {
                    URL.revokeObjectURL(localImageUrl);
                }
            },
        },
    );
};

// A typed "/tool ..." line takes the deterministic path: it runs the named tool
// within the request, so there is no reply to wait for and no attachment to
// carry. It still spends a message, so it sits behind the same cap check.
// The chat is created server side when none is open yet.
const sendCommand = (command: string) => {
    sending.value = true;

    router.post(
        route("ai-chat.tool-commands.store"),
        { command, chat_id: props.activeChat?.id ?? null },
        {
            preserveScroll: true,
            onSuccess: () => composer.value?.reset(),
            onFinish: () => {
                sending.value = false;
            },
        },
    );
};

// Unified composer handler: append to the open chat, or spin up a new one when
// none is active (the empty-state totem still shows the composer).
const onSend = (payload: { message: string; image: File | null }) => {
    if (limitReached.value) {
        return;
    }

    if (isAiSlashCommandDraft(payload.message)) {
        sendCommand(payload.message.trim());
        return;
    }

    if (props.activeChat) {
        // The optimistic bubble takes over immediately, so the composer can clear.
        composer.value?.reset();
        sendMessage(payload);
        return;
    }

    startNewChatWithMessage(payload);
};

const onAnswered = (): void => {
    router.reload({ only: ["activeChat", "pending"], preserveScroll: true });
};

// Suggested-prompt click from the welcome totem.
const onExample = (message: string) => {
    onSend({ message, image: null });
};

// A command example is deliberately not sent: most commands still need a domain,
// so the click fills the composer and hands the caret over.
const onCommandExample = (command: string) => {
    void composer.value?.replaceWith(command);
};

// A capability picked from the "what I can help with" popover never sends on its
// own — it only drops its ready-made question into the composer, so the user can
// still amend it. With a conversation on screen the question could belong either
// to it or to a fresh topic, so the target is asked for first.
const onCapability = (prompt: string) => {
    if (limitReached.value) {
        return;
    }

    if (hasConversation.value) {
        capabilityPrompt.value = prompt;
        return;
    }

    void composer.value?.fill(prompt);
};

const onCapabilityTarget = (target: AiCapabilityTarget) => {
    const prompt = capabilityPrompt.value;
    capabilityPrompt.value = null;

    if (prompt === null) {
        return;
    }

    if (target === "current") {
        void composer.value?.fill(prompt);
        return;
    }

    window.sessionStorage.setItem(PENDING_PROMPT_KEY, prompt);
    newChat();
};

// Empty-state skill pick: attach it right away if a chat is open, otherwise stash
// it and open a fresh chat — the stash is restored on the reloaded page.
const onSelectSkill = (skill: IAiSkillAttachment) => {
    if (props.activeChat) {
        selectedSkill.value = skill;
        return;
    }

    window.sessionStorage.setItem(PENDING_SKILL_KEY, JSON.stringify(skill));
    newChat();
};

// A skill picked from the empty-state offering is stashed, then a new chat is
// created. AiChat/index is reused for both the list and a chat (Inertia does not
// remount it), so restore on activeChat change — not just onMounted — otherwise
// the skill only attaches on a second click.
const restorePendingSkill = () => {
    if (!props.activeChat) {
        return;
    }

    const raw = window.sessionStorage.getItem(PENDING_SKILL_KEY);

    if (raw === null) {
        return;
    }

    window.sessionStorage.removeItem(PENDING_SKILL_KEY);

    try {
        selectedSkill.value = JSON.parse(raw) as IAiSkillAttachment;
    } catch {
        selectedSkill.value = null;
    }
};

// The freshly created chat is empty, so its composer is the one on the welcome
// totem. Restored on activeChat change for the same reason as the skill above.
const restorePendingPrompt = async (): Promise<void> => {
    if (!props.activeChat) {
        return;
    }

    const prompt = window.sessionStorage.getItem(PENDING_PROMPT_KEY);

    if (prompt === null) {
        return;
    }

    window.sessionStorage.removeItem(PENDING_PROMPT_KEY);

    // On a cold load this runs before the composer is mounted.
    await nextTick();
    await composer.value?.fill(prompt);
};

watch(
    () => props.activeChat?.id,
    () => {
        restorePendingSkill();
        void restorePendingPrompt();
    },
    { immediate: true },
);
</script>
